// Bizimhesap-tarzi Urun Yonetim Sayfasi (Faz S, 2026-05-03)
// 5 ust-buton (Yeni / Excel Yukle / Toplu Guncelle / Toplu Resim / Toplu Sil)
// + filtre tabs (Aktif/Tum) + kategori + marka + arama
// + checkbox toplu islem + mavi pill liste tasarimi
// + Excel Import dialog + Toplu Resim Yukleme dialog (3-adim)
// Mik-San brand renkleri (mavi #1565C0) + Bizimhesap layout/akis

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent, TextField,
  DialogActions, Chip, IconButton, MenuItem, InputAdornment, Tooltip, Checkbox,
  Tabs, Tab, Switch, FormControlLabel, CircularProgress, Stack, Alert,
  LinearProgress, Divider, Autocomplete
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RestoreIcon from "@mui/icons-material/Restore";
import UploadIcon from "@mui/icons-material/Upload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UpdateIcon from "@mui/icons-material/Update";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  getProducts, getCategories,
  adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  adminRestoreProduct, adminListProducts, adminUploadProductImage,
  adminBulkDeleteProducts, adminGetBrands, adminGetUnits,
  importCsv, downloadExport
} from "../services/api";
import toast, { Toaster } from "react-hot-toast";

// ============================================================================
// Sabitler
// ============================================================================

// Birim default'u BOS — kullanici Autocomplete ile mevcut listeden secer
// veya yeni bir birim yazar. Adet'in default olmasi yanilticiydi (urunlerde
// en cok Koli kullaniliyor: 224 Koli vs 19 Adet).
const EMPTY = {
  name: "", description: "", price: "", stock: "0", category_id: "",
  unit: "", tax_rate: "20", min_stock: "0", barcode: "", sku: "",
  bizimhesap_id: "", image_url: "", brand: "", is_active: true
};

// Bizimhesap-tarzi buton renkleri (Mik-San brand mavi'sini ana renk olarak korur)
const BTN_NEW       = { bgcolor: "#16A34A", "&:hover": { bgcolor: "#15803D" } }; // yesil
const BTN_IMPORT    = { bgcolor: "#0891B2", "&:hover": { bgcolor: "#0E7490" } }; // turkuaz
const BTN_UPDATE    = { bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }; // Mik-San mavi
const BTN_PHOTO     = { bgcolor: "#0D47A1", "&:hover": { bgcolor: "#082B6E" } }; // koyu mavi
const BTN_DELETE    = { bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" } }; // kirmizi

// Liste satirinda urun adinin tasiyacagi pill rengi (Bizimhesap acik mavi)
const ROW_PILL_BG = "linear-gradient(90deg, #5BA8DD 0%, #4A93C9 100%)";

// ============================================================================

export default function Products() {
  // --- Veri state'leri ---
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- Filtre state'leri ---
  const [activeTab, setActiveTab] = useState(0); // 0=Aktif Urunler, 1=Tum Urunler (silinmisler dahil)
  const [filterCategory, setFilterCategory] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [search, setSearch] = useState("");

  // --- Tek urun ekle/duzenle ---
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // --- Tekli silme onay ---
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // --- Toplu islem ---
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // --- Excel Import / Toplu Guncelleme dialog ---
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState("import"); // "import" or "update"
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);

  // --- Toplu Resim Yukleme dialog (Bizimhesap-tarzi 3-adim) ---
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const photoFileRef = useRef(null);

  // ==========================================================================
  // Veri yukleme
  // ==========================================================================

  const load = () => {
    setLoading(true);
    // activeTab 0 = sadece aktif (silinmemisler), 1 = tum (silinmisler dahil)
    const params = activeTab === 1 ? { deleted: "1", limit: 1000 } : { limit: 1000 };
    adminListProducts(params)
      .then(r => setProducts(r.data?.data || []))
      .catch(() => getProducts().then(r => setProducts(r.data)).catch(() => {}))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeTab]);

  useEffect(() => {
    getCategories().then(r => setCategories(r.data || [])).catch(() => {});
    adminGetBrands().then(r => setBrands(r.data?.brands || [])).catch(() => {});
    adminGetUnits().then(r => setUnits(r.data?.units || [])).catch(() => {});
  }, []);

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "";

  // ==========================================================================
  // Filtreleme (memo)
  // ==========================================================================

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return products.filter(p => {
      // Kategori
      if (filterCategory && String(p.category_id) !== String(filterCategory)) return false;
      // Marka
      if (filterBrand && (p.brand || "") !== filterBrand) return false;
      // Arama (min 3 karakter)
      if (q && q.length >= 3) {
        const hay = [p.name, p.sku, p.barcode, getCategoryName(p.category_id), p.brand]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, search, filterCategory, filterBrand, categories]);

  // ==========================================================================
  // Tek urun islemleri
  // ==========================================================================

  const handleEdit = (p) => {
    setForm({
      name: p.name || "",
      description: p.description || "",
      price: p.price ?? "",
      stock: p.stock ?? "0",
      category_id: p.category_id || "",
      unit: p.unit || "Adet",
      tax_rate: p.tax_rate ?? "20",
      min_stock: p.min_stock ?? "0",
      barcode: p.barcode || "",
      sku: p.sku || "",
      bizimhesap_id: p.bizimhesap_id || "",
      image_url: p.image_url || "",
      brand: p.brand || "",
      is_active: p.is_active !== 0
    });
    setEditing(p.id);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return toast.error("Ürün adı zorunlu");
    if (form.price === "" || isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0)
      return toast.error("Geçerli bir fiyat girin");
    setSaving(true);
    try {
      const payload = {
        ...form,
        is_active: form.is_active ? 1 : 0,
        price: parseFloat(form.price),
        stock: form.stock === "" ? 0 : parseInt(form.stock, 10),
        min_stock: form.min_stock === "" ? 0 : parseInt(form.min_stock, 10),
        tax_rate: form.tax_rate === "" ? 20 : parseFloat(form.tax_rate),
        category_id: form.category_id || null
      };
      if (editing) await adminUpdateProduct(editing, payload);
      else await adminCreateProduct(payload);
      toast.success(editing ? "Ürün güncellendi" : "Ürün eklendi");
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
      // Marka listesini de tazele (yeni marka eklenmiş olabilir)
      adminGetBrands().then(r => setBrands(r.data?.brands || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.error || "Hata oluştu");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await adminDeleteProduct(id);
      toast.success("Ürün silindi (sipariş geçmişi etkilenmez)");
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Silme başarısız");
    }
  };

  const handleRestore = async (id) => {
    try {
      await adminRestoreProduct(id);
      toast.success("Ürün geri yüklendi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Geri yükleme başarısız");
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Dosya 5 MB sınırını aşıyor");
    setUploading(true);
    try {
      const r = await adminUploadProductImage(file);
      setForm(f => ({ ...f, image_url: r.data.url }));
      toast.success("Görsel yüklendi");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Yükleme başarısız");
    } finally { setUploading(false); }
  };

  // ==========================================================================
  // Toplu islemler
  // ==========================================================================

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const r = await adminBulkDeleteProducts(Array.from(selected));
      toast.success(`${r.data.affected} ürün silindi (atlanan: ${r.data.skipped})`);
      setBulkDeleteOpen(false);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Toplu silme başarısız");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ==========================================================================
  // Excel Import / Toplu Guncelleme
  // ==========================================================================

  const openImportDialog = (mode) => {
    setImportMode(mode); // "import" or "update"
    setImportResult(null);
    setImportOpen(true);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv") && !file.name.toLowerCase().endsWith(".xlsx") && !file.name.toLowerCase().endsWith(".xls")) {
      return toast.error("CSV veya Excel dosyası yükleyin");
    }
    if (file.size > 5 * 1024 * 1024) return toast.error("Dosya 5 MB sınırını aşıyor");
    setImporting(true);
    setImportResult(null);
    try {
      const r = await importCsv("products", file);
      setImportResult(r.data);
      const msg = `${r.data.imported || 0} eklendi, ${r.data.updated || 0} güncellendi, ${r.data.skipped || 0} atlandı`;
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Yükleme başarısız");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadProducts = async () => {
    try {
      const blob = await downloadExport("products");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "urunler-" + new Date().toISOString().slice(0, 10) + ".csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Ürün listesi indirildi");
    } catch (e) {
      toast.error("İndirme hatası");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const r = await fetch(
        "https://api.miksanplastik.com.tr/api/admin/export/templates/products",
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "urun-sablonu.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Boş şablon indirildi");
    } catch (e) {
      toast.error("Şablon indirilemedi");
    }
  };

  // ==========================================================================
  // Toplu Resim Yukleme (Bizimhesap-tarzi 3-adim akis)
  // ==========================================================================

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoUploading(true);
    setPhotoResult(null);
    try {
      // Mevcut /api/admin/import/products endpoint'i image_url field'i okuyup
      // products.image_url'i UPDATE eder (upsert mantigi). Bizimhesap-tarzi
      // "Toplu Resim Yukleme" akisi icin ayni endpoint yeterli.
      const r = await importCsv("products", file);
      setPhotoResult(r.data);
      toast.success(`${r.data.updated || 0} ürünün resmi güncellendi`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Yükleme başarısız");
    } finally {
      setPhotoUploading(false);
    }
  };

  // ==========================================================================
  // Yardimci render fonksiyonlari
  // ==========================================================================

  const stockChip = (p) => {
    const s = Number(p.stock);
    const unit = p.unit || "ad";
    if (s <= 0) return <Typography sx={{ color: "#9ca3af", fontSize: 13 }}>0 {unit}</Typography>;
    return <Typography sx={{ color: "#374151", fontSize: 13, fontWeight: 500 }}>{s.toLocaleString("tr-TR")} {unit}</Typography>;
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Faz BD (2026-05-10): Inline quick-edit (price / stock)
  // Hücreye tıkla → TextField açılır → Enter veya blur kaydet → Esc iptal
  // ──────────────────────────────────────────────────────────────────────────
  const [inlineEdit, setInlineEdit] = useState(null); // { productId, field, value, saving }

  const startInlineEdit = (p, field) => {
    if (p.deleted_at) return;  // silinmiş ürün düzenlenmesin
    setInlineEdit({ productId: p.id, field, value: String(p[field] ?? ''), saving: false });
  };
  const cancelInlineEdit = () => setInlineEdit(null);

  const saveInlineEdit = async () => {
    if (!inlineEdit || inlineEdit.saving) return;
    const { productId, field, value } = inlineEdit;
    const numericValue = field === 'price' ? parseFloat(value) : parseInt(value, 10);

    // Aynı değer ise no-op
    const original = products.find(p => p.id === productId);
    if (original && Number(original[field]) === numericValue) {
      cancelInlineEdit();
      return;
    }
    if (isNaN(numericValue) || numericValue < 0) {
      // Geçersiz girdi — sadece dialog kapat, değiştirme
      cancelInlineEdit();
      return;
    }

    setInlineEdit(prev => ({ ...prev, saving: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`https://api.miksanplastik.com.tr/api/admin/products/${productId}/quick`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: numericValue })
      });
      const data = await res.json();
      if (!res.ok) {
        alert('Güncelleme hatası: ' + (data.error || res.status));
        cancelInlineEdit();
        return;
      }
      // Lokal state'i güncelle (re-fetch yapmadan)
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, [field]: numericValue } : p));
      cancelInlineEdit();
    } catch (e) {
      alert('Güncelleme başarısız: ' + e.message);
      cancelInlineEdit();
    }
  };

  const renderInlineCell = (p, field, displayFn) => {
    const isEditing = inlineEdit && inlineEdit.productId === p.id && inlineEdit.field === field;
    if (isEditing) {
      return (
        <TextField
          autoFocus size="small" type="number"
          value={inlineEdit.value}
          onChange={(e) => setInlineEdit(prev => ({ ...prev, value: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveInlineEdit();
            else if (e.key === 'Escape') cancelInlineEdit();
          }}
          onBlur={saveInlineEdit}
          disabled={inlineEdit.saving}
          inputProps={{ min: 0, step: field === 'price' ? '0.01' : '1', style: { textAlign: 'right', padding: '4px 6px', fontSize: 13 } }}
          sx={{ width: 90 }}
        />
      );
    }
    return (
      <Tooltip title="Tıkla → düzenle (Enter kaydet, Esc iptal)">
        <Box onClick={() => startInlineEdit(p, field)}
             sx={{ cursor: p.deleted_at ? 'default' : 'pointer',
                   '&:hover': { bgcolor: p.deleted_at ? 'transparent' : '#f1f5f9', borderRadius: 1 } }}>
          {displayFn(p)}
        </Box>
      </Tooltip>
    );
  };

  // Bizimhesap-tarzi liste satiri: solda checkbox, sonra image, sonra mavi pill
  // arkaplanlı urun adı + inline kategori/marka chip'leri, sagda fiyat + stok
  const renderProductRow = (p) => {
    const isSelected = selected.has(p.id);
    const isDeleted = !!p.deleted_at;
    const cat = getCategoryName(p.category_id);

    return (
      <Box
        key={p.id}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderBottom: "1px solid #e5e7eb",
          opacity: isDeleted ? 0.5 : 1,
          backgroundColor: isSelected ? "#EFF6FF" : "transparent",
          "&:hover": { backgroundColor: isSelected ? "#DBEAFE" : "#F9FAFB" },
          transition: "background-color 0.1s"
        }}
      >
        {/* Checkbox */}
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => toggleSelect(p.id)}
          disabled={isDeleted}
          sx={{ p: 0.5 }}
        />

        {/* Görsel */}
        {p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, border: "1px solid #e5e7eb" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <Box sx={{ width: 36, height: 36, bgcolor: "#f3f4f6", borderRadius: 1, flexShrink: 0 }} />
        )}

        {/* Mavi pill: ürün adı + inline kategori + marka */}
        <Box
          onClick={() => !isDeleted && handleEdit(p)}
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: ROW_PILL_BG,
            borderRadius: 1,
            px: 1.5,
            py: 1,
            color: "#fff",
            cursor: isDeleted ? "default" : "pointer",
            "&:hover": isDeleted ? {} : { filter: "brightness(0.95)" }
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
            title={p.name}
          >
            {p.name}
          </Typography>
          {cat && (
            <Chip
              label={cat}
              size="small"
              sx={{
                bgcolor: "#FCD34D",
                color: "#78350F",
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                "& .MuiChip-label": { px: 1 }
              }}
            />
          )}
          {p.brand && (
            <Chip
              label={p.brand}
              size="small"
              sx={{
                bgcolor: "#FB923C",
                color: "#7C2D12",
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                "& .MuiChip-label": { px: 1 }
              }}
            />
          )}
          {isDeleted && (
            <Chip label="SİLİNMİŞ" size="small" sx={{ bgcolor: "#FEE2E2", color: "#B91C1C", fontWeight: 700, fontSize: 10, height: 20 }} />
          )}
        </Box>

        {/* Fiyat — Faz BD: inline edit */}
        <Box sx={{ minWidth: 110, textAlign: "right" }}>
          {renderInlineCell(p, 'price', (pp) => (
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1565C0" }}>
              {Number(pp.price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL
            </Typography>
          ))}
        </Box>

        {/* Stok — Faz BD: inline edit */}
        <Box sx={{ minWidth: 90, textAlign: "right" }}>
          {renderInlineCell(p, 'stock', stockChip)}
        </Box>

        {/* İşlem */}
        <Box sx={{ minWidth: 80, display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
          {isDeleted ? (
            <Tooltip title="Geri yükle">
              <IconButton size="small" onClick={() => handleRestore(p.id)} sx={{ color: "#10B981" }}>
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Düzenle">
                <IconButton size="small" onClick={() => handleEdit(p)} sx={{ color: "#1565C0" }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sil">
                <IconButton size="small" onClick={() => setDeleteConfirm(p)} sx={{ color: "#EF4444" }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    );
  };

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <Box>
      <Toaster />

      {/* Sayfa başlığı */}
      <Typography variant="h5" fontWeight={700} mb={2}>Ürünler</Typography>

      {/* TOOLBAR — Bizimhesap-tarzı 5 buton */}
      <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} useFlexGap>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }}
          sx={BTN_NEW}
        >
          Yeni Ürün/Hizmet Ekle
        </Button>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => openImportDialog("import")}
          sx={BTN_IMPORT}
        >
          Excelden Ürün Yükle
        </Button>
        <Button
          variant="contained"
          startIcon={<UpdateIcon />}
          onClick={() => openImportDialog("update")}
          sx={BTN_UPDATE}
        >
          Toplu Ürün Güncelleme
        </Button>
        <Button
          variant="contained"
          startIcon={<PhotoCameraIcon />}
          onClick={() => { setPhotoResult(null); setPhotoOpen(true); }}
          sx={BTN_PHOTO}
        >
          Toplu Resim Yükleme
        </Button>
        <Button
          variant="contained"
          startIcon={<DeleteSweepIcon />}
          onClick={() => setBulkDeleteOpen(true)}
          disabled={selected.size === 0}
          sx={BTN_DELETE}
        >
          Toplu Ürün Silme {selected.size > 0 && `(${selected.size})`}
        </Button>
      </Stack>

      {/* FILTRE BAR — Tabs + Kategori + Marka + Arama */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => { setActiveTab(v); setSelected(new Set()); }}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab label="Aktif Ürünler" />
          <Tab label="Tüm Ürünler" />
        </Tabs>
        <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            select
            size="small"
            label="Tüm kategoriler"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Tüm kategoriler</MenuItem>
            {categories.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Tüm markalar"
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Tüm markalar</MenuItem>
            {brands.map(b => (
              <MenuItem key={b.brand} value={b.brand}>
                {b.brand} ({b.product_count})
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            placeholder="Arama (en az 3 karakter)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
        </Box>
      </Paper>

      {/* Toplu seçim özeti */}
      {selected.size > 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setSelected(new Set())}>
          <strong>{selected.size}</strong> ürün seçildi —{" "}
          <Button size="small" onClick={() => setSelected(new Set())}>Temizle</Button>
        </Alert>
      )}

      {/* LİSTE BAŞLIK SATIRI */}
      <Paper variant="outlined">
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          backgroundColor: "#1f2937",
          color: "#fff",
          fontSize: 13,
          fontWeight: 600
        }}>
          <Checkbox
            size="small"
            checked={filtered.length > 0 && selected.size === filtered.length}
            indeterminate={selected.size > 0 && selected.size < filtered.length}
            onChange={toggleSelectAll}
            sx={{ p: 0.5, color: "#fff", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" } }}
          />
          <Box sx={{ width: 36 }} />
          <Box sx={{ flex: 1 }}>Ürün/Hizmet Adı</Box>
          <Box sx={{ minWidth: 110, textAlign: "right" }}>Satış Fiyatı</Box>
          <Box sx={{ minWidth: 90, textAlign: "right" }}>Stok Miktarı</Box>
          <Box sx={{ minWidth: 80 }} />
        </Box>

        {/* Yükleniyor */}
        {loading && <LinearProgress />}

        {/* Boş durumu */}
        {!loading && filtered.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            {search.length >= 3 || filterCategory || filterBrand
              ? "Filtre sonucu eşleşen ürün bulunamadı"
              : (activeTab === 1 ? "Hiç ürün yok" : "Aktif ürün bulunamadı")}
          </Box>
        )}

        {/* Liste */}
        <Box sx={{ maxHeight: "calc(100vh - 360px)", overflow: "auto" }}>
          {filtered.map(p => renderProductRow(p))}
        </Box>

        {/* Alt bilgi */}
        {filtered.length > 0 && (
          <Box sx={{ p: 1.5, borderTop: "1px solid #e5e7eb", color: "text.secondary", fontSize: 12 }}>
            Toplam {filtered.length} ürün gösteriliyor
            {(filterCategory || filterBrand || search) && ` (filtreli)`}
          </Box>
        )}
      </Paper>

      {/* ==================================================================== */}
      {/* DIALOG: Yeni / Düzenle (mevcut form korunur, brand alanı eklendi) */}
      {/* ==================================================================== */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Ürün Düzenle" : "Yeni Ürün/Hizmet Ekle"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="Ürün Adı *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth required />
          <TextField label="Açıklama" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} fullWidth multiline rows={3} />
          <Box display="flex" gap={2}>
            <TextField select label="Kategori" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} fullWidth>
              <MenuItem value="">— Yok —</MenuItem>
              {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
            </TextField>
            <TextField label="Marka" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} fullWidth helperText="Örn: FLOPPY, LİMERA, MİKSAN" />
          </Box>
          <Box display="flex" gap={2}>
            {/* Birim — DB'deki distinct listeden secim, freeSolo (yeni birim de yazilabilir) */}
            <Autocomplete
              freeSolo
              options={units.map(u => u.unit)}
              value={form.unit || ""}
              onChange={(e, newVal) => setForm({ ...form, unit: newVal || "" })}
              onInputChange={(e, newVal) => setForm({ ...form, unit: newVal || "" })}
              fullWidth
              renderOption={(props, option) => {
                const u = units.find(x => x.unit === option);
                return (
                  <li {...props} key={option}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <span>{option}</span>
                      {u && <span style={{ color: "#9ca3af", fontSize: 12 }}>{u.product_count} ürün</span>}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Birim"
                  placeholder="Koli, Adet, Paket, Kilogram..."
                  helperText="Mevcut listeden seçin veya yeni birim yazın"
                />
              )}
            />
            <TextField label="KDV Oranı" type="number" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} fullWidth
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} helperText="0-25 arası" />
          </Box>
          <Box display="flex" gap={2}>
            <TextField label="Fiyat (₺) *" type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} fullWidth required
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }} />
            <TextField label="Stok" type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} fullWidth />
            <TextField label="Min Stok" type="number" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} fullWidth helperText="Bu sayının altına düşerse uyarı" />
          </Box>
          <Box display="flex" gap={2}>
            <TextField label="Barkod" value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} fullWidth />
            <TextField label="SKU" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} fullWidth />
          </Box>
          <TextField
            label="BizimHesap Ürün ID"
            value={form.bizimhesap_id}
            onChange={e => setForm({ ...form, bizimhesap_id: e.target.value })}
            fullWidth
            helperText="BH'de fatura kesimek için gerekli. Yoksa boş bırakın, BH yeni ürün gibi görür ve fatura kesim sırasında uyarı verir."
          />
          {/* Görsel upload */}
          <Box sx={{ p: 2, border: "1px dashed #ccc", borderRadius: 1, display: "flex", gap: 2, alignItems: "center" }}>
            {form.image_url ? (
              <img src={form.image_url} alt="Ürün" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4 }} />
            ) : (
              <Box sx={{ width: 80, height: 80, bgcolor: "#f5f5f5", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#999" }}>—</Box>
            )}
            <Box flex={1}>
              <Button
                component="label"
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploading}
              >
                {uploading ? "Yükleniyor..." : "Görsel Yükle"}
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} />
              </Button>
              {form.image_url && (
                <Button size="small" sx={{ ml: 1 }} color="error" onClick={() => setForm(f => ({ ...f, image_url: "" }))}>Kaldır</Button>
              )}
              <Typography variant="caption" display="block" mt={1} color="text.secondary">
                Maks 5 MB · JPG / PNG / WEBP
              </Typography>
            </Box>
          </Box>
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />}
            label="Aktif (mobil + web sitesinde gösterilsin)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>İptal</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: "#1565C0" }} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================================== */}
      {/* DIALOG: Tek Ürün Silme Onay */}
      {/* ==================================================================== */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ürünü Sil</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{deleteConfirm?.name}</strong> ürününü silmek istediğinizden emin misiniz?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Soft delete uygulanır — ürün listede görünmez ama sipariş geçmişi etkilenmez.
            "Tüm Ürünler" sekmesinden geri yükleyebilirsiniz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>İptal</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?.id)}>
            Evet, Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================================== */}
      {/* DIALOG: Toplu Ürün Silme */}
      {/* ==================================================================== */}
      <Dialog open={bulkDeleteOpen} onClose={() => !bulkDeleting && setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Toplu Ürün Silme</DialogTitle>
        <DialogContent>
          <Typography>
            Seçili <strong>{selected.size}</strong> ürünü silmek istediğinizden emin misiniz?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Soft delete uygulanır — sipariş geçmişi etkilenmez. "Tüm Ürünler" sekmesinden geri yükleyebilirsiniz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>İptal</Button>
          <Button variant="contained" color="error" onClick={handleBulkDelete} disabled={bulkDeleting}>
            {bulkDeleting ? "Siliniyor..." : `Evet, ${selected.size} Ürünü Sil`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================================== */}
      {/* DIALOG: Excel Import / Toplu Güncelleme (aynı endpoint, farklı CTA) */}
      {/* ==================================================================== */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {importMode === "import" ? "Excelden Ürün Yükle" : "Toplu Ürün Güncelleme"}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {importMode === "import"
              ? "CSV dosyanızdaki yeni ürünler eklenir, mevcut SKU/barkod/BH ID ile eşleşenler güncellenir."
              : "Mevcut ürünleri toplu güncellemek için. SKU/barkod/BH ID ile eşleşmeyen satırlar yeni ürün olarak eklenir."}
          </Alert>
          <Stack spacing={2}>
            <Box>
              <Typography fontWeight={600} mb={1}>1. Şablon veya mevcut ürün listesi indirin</Typography>
              <Stack direction="row" spacing={1}>
                <Button startIcon={<FileDownloadIcon />} variant="outlined" onClick={handleDownloadTemplate}>
                  Boş Şablon İndir
                </Button>
                <Button startIcon={<FileDownloadIcon />} variant="outlined" onClick={handleDownloadProducts}>
                  Mevcut Ürün Listesi İndir
                </Button>
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Typography fontWeight={600} mb={1}>2. Düzenleyin ve yükleyin</Typography>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={importing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                disabled={importing}
                onClick={() => importFileRef.current?.click()}
                sx={{ bgcolor: "#1565C0" }}
              >
                {importing ? "Yükleniyor..." : "CSV Dosyası Seç ve Yükle"}
                <input
                  ref={importFileRef}
                  type="file"
                  hidden
                  accept=".csv,.xlsx,.xls,text/csv"
                  onChange={handleImportFile}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Maks 5 MB · CSV (UTF-8) · 10.000 satır · Saatte en fazla 5 yükleme
              </Typography>
            </Box>
            {importResult && (
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography fontWeight={600} mb={1}>Sonuç</Typography>
                <Stack direction="row" spacing={1} mb={1}>
                  <Chip label={`Toplam: ${importResult.total || 0}`} size="small" />
                  <Chip label={`Eklendi: ${importResult.imported || 0}`} size="small" color="success" />
                  <Chip label={`Güncellendi: ${importResult.updated || 0}`} size="small" color="primary" />
                  <Chip label={`Atlandı: ${importResult.skipped || 0}`} size="small" color="warning" />
                </Stack>
                {(importResult.errors || []).length > 0 && (
                  <Box sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #fee2e2", borderRadius: 1, p: 1 }}>
                    <Typography variant="caption" color="error" fontWeight={600}>Hatalar:</Typography>
                    {importResult.errors.slice(0, 50).map((e, i) => (
                      <Typography key={i} variant="caption" display="block" sx={{ color: "#7f1d1d" }}>
                        Satır {e.row}: {e.message}
                      </Typography>
                    ))}
                    {importResult.errors.length > 50 && (
                      <Typography variant="caption" color="text.secondary">
                        … {importResult.errors.length - 50} hata daha
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================================== */}
      {/* DIALOG: Toplu Resim Yükleme (Bizimhesap-tarzı 3-adımlı akış) */}
      {/* ==================================================================== */}
      <Dialog open={photoOpen} onClose={() => !photoUploading && setPhotoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Toplu Resim Yükleme</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ürün resimlerini toplu güncellemek için 3 adımı takip edin.
          </Alert>
          <Stack spacing={2}>
            <Box>
              <Typography fontWeight={600} color="primary" mb={1}>1. Ürün listesini indirin</Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Mevcut ürünlerinizi indirip <code>image_url</code> kolonunu düzenleyeceksiniz.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadProducts}
                sx={{ bgcolor: "#16A34A" }}
              >
                Ürün Listesi İndir
              </Button>
            </Box>
            <Divider />
            <Box>
              <Typography fontWeight={600} color="primary" mb={1}>2. Excel'i düzenleyin</Typography>
              <Box sx={{ pl: 2, color: "text.secondary", fontSize: 13 }}>
                <Typography variant="body2">• Kolonların sırasını değiştirmeyin, kolonları silmeyin</Typography>
                <Typography variant="body2" sx={{ color: "#dc2626", fontWeight: 600 }}>
                  • Resim URL'leri <strong>https://</strong> ile başlamalı (SSL'siz dosyalar yüklenmez)
                </Typography>
                <Typography variant="body2">• Format: jpg, jpeg, gif, png</Typography>
                <Typography variant="body2">• Yeni satır eklemeyin (sadece mevcut ürünleri günceller)</Typography>
                <Typography variant="body2">• Önceki resim varsa silinir, yeni resim geçerli olur</Typography>
              </Box>
            </Box>
            <Divider />
            <Box>
              <Typography fontWeight={600} color="primary" mb={1}>3. Düzenlenmiş Excel'i yükleyin</Typography>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={photoUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                disabled={photoUploading}
                onClick={() => photoFileRef.current?.click()}
                sx={{ bgcolor: "#0D47A1" }}
              >
                {photoUploading ? "Yükleniyor..." : "Excel Dosyasını Yükle"}
                <input
                  ref={photoFileRef}
                  type="file"
                  hidden
                  accept=".csv,.xlsx,.xls,text/csv"
                  onChange={handlePhotoFile}
                />
              </Button>
            </Box>
            {photoResult && (
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography fontWeight={600} mb={1}>Sonuç</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label={`Toplam: ${photoResult.total || 0}`} size="small" />
                  <Chip label={`Resim güncellendi: ${photoResult.updated || 0}`} size="small" color="success" />
                  <Chip label={`Atlandı: ${photoResult.skipped || 0}`} size="small" color="warning" />
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoOpen(false)} disabled={photoUploading}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
